import { useTranslation } from 'react-i18next';
import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { 
  Search, GraduationCap, Calendar, AlertTriangle, FileText, 
  CheckCircle, XCircle, Clock, ExternalLink, ChevronDown, ChevronUp
} from 'lucide-react';
import { format } from 'date-fns';
import { DateText } from '../ui/week-badge';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const EmployeeISOInfo = ({ 
  employees = [], 
  training = [], 
  audits = [], 
  ncrs = [], 
  documents = [],
  token,
  initialSearch,
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState(initialSearch || '');
  const [expandedEmployee, setExpandedEmployee] = useState(null);

  // When the parent navigates here with a different employee (e.g. user
  // clicks several avatars in a row), sync the search box.
  React.useEffect(() => {
    if (initialSearch) setSearchTerm(initialSearch);
  }, [initialSearch]);

  // Filter employees based on search
  const filteredEmployees = useMemo(() => {
    if (!searchTerm) return employees;
    const term = searchTerm.toLowerCase();
    return employees.filter(emp => 
      emp.name?.toLowerCase().includes(term) ||
      emp.email?.toLowerCase().includes(term) ||
      emp.department?.toLowerCase().includes(term)
    );
  }, [employees, searchTerm]);

  // If the search narrowed down to exactly one employee (typically because
  // we landed here via the avatar click on the Employees tab), auto-expand
  // that row so the ISO records are immediately visible.
  React.useEffect(() => {
    if (filteredEmployees.length === 1 && searchTerm) {
      setExpandedEmployee(filteredEmployees[0].id);
    }
  }, [filteredEmployees, searchTerm]);

  // Get ISO-related info for an employee
  const getEmployeeISOData = (employeeId) => {
    // Training records where employee is a participant
    const employeeTrainings = training.filter(t => 
      t.employee_ids?.includes(employeeId) || t.trainer_id === employeeId
    );
    
    // Audits where employee is involved
    const employeeAudits = audits.filter(a => 
      a.lead_auditor_id === employeeId ||
      a.auditor_ids?.includes(employeeId) ||
      a.auditee_ids?.includes(employeeId)
    );
    
    // NCRs related to employee
    const employeeNCRs = ncrs.filter(n => 
      n.detected_by_id === employeeId ||
      n.linked_employee_ids?.includes(employeeId)
    );
    
    // Documents where employee is author, reviewer, or approver
    const employeeDocuments = documents.filter(d => 
      d.author_id === employeeId ||
      d.reviewer_id === employeeId ||
      d.approver_id === employeeId ||
      d.linked_employee_ids?.includes(employeeId)
    );

    return { 
      trainings: employeeTrainings, 
      audits: employeeAudits, 
      ncrs: employeeNCRs,
      documents: employeeDocuments 
    };
  };

  // Get training status badge
  const getTrainingStatusBadge = (training, employeeId) => {
    const result = training.participant_results?.[employeeId];
    if (!result) {
      return <Badge variant="outline">{t('auto.iso.enrolled')}</Badge>;
    }
    switch (result.status) {
      case 'passed':
        return <Badge className="bg-green-100 text-green-800">{t('auto.iso.passed')}</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800">{t('auto.iso.failed')}</Badge>;
      case 'attended':
        return <Badge className="bg-blue-100 text-blue-800">{t('auto.iso.attended')}</Badge>;
      case 'excused':
        return <Badge variant="secondary">{t('auto.iso.excused')}</Badge>;
      default:
        return <Badge variant="outline">{t('auto.iso.enrolled')}</Badge>;
    }
  };

  // Get training score display
  const getTrainingScore = (training, employeeId) => {
    const result = training.participant_results?.[employeeId];
    if (!result || result.score === null || result.score === undefined) {
      return null;
    }
    
    const percentage = result.score;
    const passingScore = training.passing_score || 80;
    const passed = percentage >= passingScore;
    
    return (
      <span className={`text-sm font-medium ${passed ? 'text-green-600' : 'text-red-600'}`}>
        {percentage}% {passed ? '✓' : '✗'}
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="employee-iso-info">
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-blue-600" />
                {t('auto.iso.employeeIsoInformation')}
              </CardTitle>
              <CardDescription>
                {t('auto.iso.viewTrainingRecordsAuditParticipationAnd')}
              </CardDescription>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder={t('auto.iso.searchEmployees')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="employee-iso-search"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {filteredEmployees.length === 0 ? (
              <p className="text-center text-gray-500 py-8">{t('auto.iso.noEmployeesFound')}</p>
            ) : (
              filteredEmployees.map((employee) => {
                const isoData = getEmployeeISOData(employee.id);
                const isExpanded = expandedEmployee === employee.id;
                const hasISOActivity = 
                  isoData.trainings.length > 0 || 
                  isoData.audits.length > 0 || 
                  isoData.ncrs.length > 0 ||
                  isoData.documents.length > 0;

                return (
                  <div 
                    key={employee.id} 
                    className="border rounded-lg overflow-hidden"
                    data-testid={`employee-iso-card-${employee.id}`}
                  >
                    {/* Employee Header */}
                    <div 
                      className={`p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 ${isExpanded ? 'bg-gray-50' : ''}`}
                      onClick={() => setExpandedEmployee(isExpanded ? null : employee.id)}
                    >
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={employee.avatar} alt={employee.name} />
                          <AvatarFallback className="bg-blue-100 text-blue-600">
                            {employee.name?.charAt(0)?.toUpperCase() || '?'}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <h3 className="font-medium text-gray-900">{employee.name}</h3>
                          <p className="text-sm text-gray-500">
                            {employee.department || 'No department'} {employee.position ? `• ${employee.position}` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {/* Summary badges */}
                        <div className="hidden sm:flex items-center gap-2">
                          {isoData.trainings.length > 0 && (
                            <Badge variant="outline" className="gap-1">
                              <GraduationCap className="h-3 w-3" />
                              {isoData.trainings.length}
                            </Badge>
                          )}
                          {isoData.audits.length > 0 && (
                            <Badge variant="outline" className="gap-1">
                              <Calendar className="h-3 w-3" />
                              {isoData.audits.length}
                            </Badge>
                          )}
                          {isoData.ncrs.length > 0 && (
                            <Badge variant="outline" className="gap-1 text-amber-600">
                              <AlertTriangle className="h-3 w-3" />
                              {isoData.ncrs.length}
                            </Badge>
                          )}
                          {isoData.documents.length > 0 && (
                            <Badge variant="outline" className="gap-1">
                              <FileText className="h-3 w-3" />
                              {isoData.documents.length}
                            </Badge>
                          )}
                        </div>
                        {hasISOActivity ? (
                          isExpanded ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />
                        ) : (
                          <span className="text-sm text-gray-400">{t('auto.iso.noIsoRecords')}</span>
                        )}
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {isExpanded && hasISOActivity && (
                      <div className="border-t bg-white p-4 space-y-4">
                        {/* Training Section */}
                        {isoData.trainings.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                              <GraduationCap className="h-4 w-4 text-purple-500" />
                              Training Records ({isoData.trainings.length})
                            </h4>
                            <div className="space-y-2">
                              {isoData.trainings.map((tItem) => (
                                <div 
                                  key={tItem.id} 
                                  className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm"
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{tItem.title}</span>
                                    {tItem.url && (
                                      <a 
                                        href={tItem.url.startsWith('http') ? tItem.url : `https://${tItem.url}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-800"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <ExternalLink className="h-3 w-3" />
                                      </a>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {getTrainingScore(tItem, employee.id)}
                                    {getTrainingStatusBadge(tItem, employee.id)}
                                    {tItem.trainer_id === employee.id && (
                                      <Badge variant="secondary">{t('auto.iso.trainer')}</Badge>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Audit Section */}
                        {isoData.audits.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-blue-500" />
                              Audit Participation ({isoData.audits.length})
                            </h4>
                            <div className="space-y-2">
                              {isoData.audits.map((a) => (
                                <div 
                                  key={a.id} 
                                  className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm"
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{a.title}</span>
                                    <span className="text-gray-500">({a.audit_number})</span>
                                    {a.url && (
                                      <a 
                                        href={a.url.startsWith('http') ? a.url : `https://${a.url}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-800"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <ExternalLink className="h-3 w-3" />
                                      </a>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-gray-500">
                                      {a.planned_date ? <DateText value={a.planned_date} /> : 'No date'}
                                    </span>
                                    {a.lead_auditor_id === employee.id && (
                                      <Badge className="bg-blue-100 text-blue-800">{t('auto.iso.leadAuditor2')}</Badge>
                                    )}
                                    {a.auditor_ids?.includes(employee.id) && a.lead_auditor_id !== employee.id && (
                                      <Badge variant="outline">{t('auto.iso.auditor')}</Badge>
                                    )}
                                    {a.auditee_ids?.includes(employee.id) && (
                                      <Badge variant="secondary">{t('auto.iso.auditee')}</Badge>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* NCR Section */}
                        {isoData.ncrs.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                              <AlertTriangle className="h-4 w-4 text-amber-500" />
                              NCR Records ({isoData.ncrs.length})
                            </h4>
                            <div className="space-y-2">
                              {isoData.ncrs.map((n) => (
                                <div 
                                  key={n.id} 
                                  className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm"
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{n.title}</span>
                                    <span className="text-gray-500">({n.ncr_number})</span>
                                    {n.url && (
                                      <a 
                                        href={n.url.startsWith('http') ? n.url : `https://${n.url}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-800"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <ExternalLink className="h-3 w-3" />
                                      </a>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge 
                                      className={
                                        n.severity === 'critical' ? 'bg-red-100 text-red-800' :
                                        n.severity === 'major' ? 'bg-orange-100 text-orange-800' :
                                        'bg-yellow-100 text-yellow-800'
                                      }
                                    >
                                      {n.severity}
                                    </Badge>
                                    {n.detected_by_id === employee.id && (
                                      <Badge variant="outline">{t('auto.iso.reporter')}</Badge>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Documents Section */}
                        {isoData.documents.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                              <FileText className="h-4 w-4 text-green-500" />
                              Document Involvement ({isoData.documents.length})
                            </h4>
                            <div className="space-y-2">
                              {isoData.documents.map((d) => (
                                <div 
                                  key={d.id} 
                                  className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm"
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{d.title}</span>
                                    <span className="text-gray-500">({d.document_number})</span>
                                    {d.url && (
                                      <a 
                                        href={d.url.startsWith('http') ? d.url : `https://${d.url}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-800"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <ExternalLink className="h-3 w-3" />
                                      </a>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {d.author_id === employee.id && (
                                      <Badge className="bg-green-100 text-green-800">{t('auto.iso.author')}</Badge>
                                    )}
                                    {d.reviewer_id === employee.id && (
                                      <Badge className="bg-blue-100 text-blue-800">{t('auto.iso.reviewer')}</Badge>
                                    )}
                                    {d.approver_id === employee.id && (
                                      <Badge className="bg-purple-100 text-purple-800">{t('auto.iso.approver')}</Badge>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmployeeISOInfo;
